/**
 * One reconciliation point for "is there unread here", shared by every surface
 * that shows it (#1069 P3.3).
 *
 * ⚠️ THIS EXISTS BECAUSE THE SAME BUG HAS NOW BEEN REPORTED THREE TIMES AT
 * THREE LEVELS, AND THE COMMON CAUSE WAS NEVER THE LEVEL IT WAS REPORTED AT:
 *
 *   1. the THREAD CARD's dot did not clear when its own thread was opened
 *      (caught in P3.2 review);
 *   2. the SPACE TAB's dot did not clear when every thread had been read
 *      (owner, 2026-08-25);
 *   3. the LAUNCHER's dot did not clear after reading and closing the panel,
 *      and the tab then flashed "New" on reopen before correcting itself
 *      (owner, 2026-08-25).
 *
 * Each was fixed at the level it was seen, and each fix pushed the same defect
 * one level outwards. **The cause is server-derived unread state rendered by a
 * surface that has no way to learn what the panel already knows.** The panel
 * makes the user's read happen; the launcher, the request row and the inactive
 * tab all render a summary fetched before it. Refetching the summary does not
 * close it — `RequestDetail.handleThreadRead` has invalidated
 * `correspondenceSummaryKeys.detail` and `requestsKeys.all` on every successful
 * `markThreadRead` since P3.2, measured — because a refetch cannot correct an
 * answer the SERVER gets wrong, and two paths make it wrong (see
 * `deferred-work.md`: `listThreads` drops index rows Flarum will not hydrate
 * while the summary counts them, and it degrades to `unread: undefined` on a
 * read-state failure the summary does not share).
 *
 * So the fix is structural rather than another patch at another level: what the
 * panel establishes LOCALLY is reported up to the screen, merged with the
 * server's answer HERE, and every surface renders the merged value. One place
 * reconciles; nowhere else has precedence rules.
 *
 * Takes plain booleans so it can live beside the components that use it, a
 * choice rather than a constraint: until Story 6.1 this directory's suites
 * mounted without a `QueryClientProvider`, and since 2026-09-16 any client
 * suite can render through `src/test-utils/render` and get a provider and a
 * settled query (`.claude/rules/testing.md`,
 * "The client timer harness: fresh legacy fakes per test, capped at 100 ms")
 * (#1069 Story 6.2, 2026-09-16).
 *
 * @see GitLab #1069 — Request Correspondence
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SpaceId, SpaceUnread } from './correspondence.types';

/** What the panel has established for itself, per space. */
type LocalAnswer = { internal?: boolean; shared?: boolean };

export interface UnreadSpaceState {
  /** Per space, merged. Feed this to the panel's space tabs. */
  spaceUnread: SpaceUnread;
  /** The union, merged. Feed this to the launcher pill. */
  hasUnread: boolean;
  /**
   * The panel calling in with what it can see. Idempotent: repeating an answer
   * changes nothing, which is what keeps it out of a render loop.
   */
  noteLocalAnswer: (space: SpaceId, hasUnread: boolean) => void;
}

export interface ServerUnread {
  /** `summary.hasUnread`. */
  hasUnread?: boolean;
  /** `summary.spaces.*.hasUnread`. */
  internal?: boolean;
  shared?: boolean;
  /**
   * Whether the server actually sent a per-space breakdown. Without one a local
   * answer cannot be attributed to a space, so the union falls back to the
   * server's own — see below.
   */
  hasSpaces?: boolean;
}

export const useUnreadSpaceState = (server: ServerUnread): UnreadSpaceState => {
  const [local, setLocal] = useState<LocalAnswer>({});

  const serverInternal = server.internal === true;
  const serverShared = server.shared === true;

  /**
   * Forget a local answer when the SERVER CHANGES ITS MIND about that space.
   *
   * ⚠️ ON A CHANGE, NOT ON A `true`. A value that has been `true` all along
   * tells us nothing new and must not resurrect a badge the user has cleared —
   * that is the sticky dot. A `false → true` transition is real news (a message
   * arrived) and a `true → false` one is the server catching up; both mean our
   * local answer is no longer the newest thing anybody knows.
   */
  const previous = useRef<{ internal: boolean; shared: boolean } | null>(null);
  useEffect(() => {
    const now = { internal: serverInternal, shared: serverShared };
    const before = previous.current;
    previous.current = now;
    if (!before) return;
    setLocal(held => {
      const next: LocalAnswer = { ...held };
      let changed = false;
      if (now.internal !== before.internal && held.internal !== undefined) {
        next.internal = undefined;
        changed = true;
      }
      if (now.shared !== before.shared && held.shared !== undefined) {
        next.shared = undefined;
        changed = true;
      }
      // Same object back when nothing moved: a new object every render would
      // re-render every consumer of this hook forever.
      return changed ? next : held;
    });
  }, [serverInternal, serverShared]);

  const noteLocalAnswer = useCallback((space: SpaceId, hasUnread: boolean) => {
    setLocal(held => {
      const current = space === 'internal' ? held.internal : held.shared;
      if (current === hasUnread) return held;
      return space === 'internal'
        ? { ...held, internal: hasUnread }
        : { ...held, shared: hasUnread };
    });
  }, []);

  const spaceUnread: SpaceUnread = {
    internal: local.internal ?? serverInternal,
    shared: local.shared ?? serverShared,
  };

  /**
   * ⚠️ THE UNION IS DERIVED FROM THE TWO SPACES, WHICH IS EXACTLY WHAT THE
   * SERVER DOES (`correspondenceUnreadSummary.ts`:
   * `hasUnread = SPACES.some(...)`), so with no local answer held this is
   * byte-identical to `summary.hasUnread`.
   *
   * ⚠️ UNLESS THERE IS NO BREAKDOWN TO DERIVE FROM. If `summary.spaces` is
   * absent, both values above are `false` and deriving would DELETE a dot the
   * server is asserting. A local answer cannot be attributed to a space in that
   * case either, so the honest answer is the server's own.
   */
  const hasUnread =
    server.hasSpaces === false
      ? server.hasUnread === true
      : spaceUnread.internal === true || spaceUnread.shared === true;

  return { spaceUnread, hasUnread, noteLocalAnswer };
};
